import * as React from 'react';
import { ICenterBase, ICenterNotification, INotificationType } from '@nkr/models/index';
import { FormGroup, ControlLabel, FormControl, Button, Alert, Checkbox } from 'react-bootstrap';
import config from '../../../ticketing/jodit-editor-config';
import styled from "styled-components";
import JoditEditor from '@nkr/components/editor';

const EditorWrapper = styled.div<{ error: boolean }>`
    .jodit_workplace {
        border: ${p => p.error ? '1px solid #a94442 !important' : ''};
    }
`;

interface IProps {
    data: ICenterNotification,
    clone: {
        isClone: boolean,
        description: string
    },
    show: boolean,
    centerList: ICenterBase[],
    notificationList: INotificationType[],
    onSave: (id: number, center: number, description: string, content: string, notificationTypeId: number, isEnabled: boolean) => void,
    onCancel: () => void
}

interface IState {
    id: number,
    center: number,
    notificationTypeId: number,
    isEnabled: boolean,
    description: string,
    content: string,
    validation: {
        notificationTypeId: boolean,
        center: boolean,
        description: boolean,
        content: boolean
    },
    dirty: boolean
}

class AddEdit extends React.Component<IProps, IState> {
    readonly state: IState = {
        id: this.props.data != null ? this.props.data.id : 0,
        center: this.props.data != null ? this.props.data.centerId : 0,
        notificationTypeId: this.props.data != null ? this.props.data.notificationTypeId : 0,
        isEnabled: this.props.data != null ? this.props.data.isEnabled : false,
        description: this.props.data != null ? this.props.data.description : '',
        content: this.props.data != null ? this.props.data.notificationText : '',
        validation: {
            notificationTypeId: true,
            center: true,
            description: true,
            content: true
        },
        dirty: false
    }

    handleNotificationChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.notificationTypeId = parseInt(e.currentTarget.value) > 0;
        }

        this.setState({
            notificationTypeId: parseInt(e.currentTarget.value),
            validation
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.center = parseInt(e.currentTarget.value) > 0;
        }

        this.setState({
            center: parseInt(e.currentTarget.value),
            validation
        });
    }

    handleDescriptionChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.description = e.currentTarget.value != '';
        }

        this.setState({
            description: e.currentTarget.value,
            validation
        });
    }

    handleContentChange = (content: string) => {
        let validation = { ...this.state.validation };

        if (this.state.dirty) {
            validation.content = content != '';
        }

        this.setState({
            content,
            validation
        });
    }

    handleSave = () => {
        let validation = this.validate();

        this.setState({
            validation,
            dirty: true
        }, () => {
            let { id, center, description, content, isEnabled, notificationTypeId } = this.state;
            let { center: centerValid, description: descriptionValid, content: contentValid } = validation;
            let valid = centerValid && descriptionValid && contentValid;

            if (valid) {
                this.props.onSave(id, center, description, content, notificationTypeId, isEnabled);
            }
        });
    }

    handleCancel = () => {
        this.props.onCancel();
    }

    validate = () => {
        let { center, description, content, notificationTypeId } = this.state;

        return {
            notificationTypeId: notificationTypeId != 0,
            center: center != 0,
            description: description != '',
            content: content != ''
        };
    }

    render = () => {
        const valid = this.state.validation.center && this.state.validation.description && this.state.validation.content;
        const contentValid = !this.state.validation.content && this.state.dirty;

        return (
            <div style={{ padding: 10 }}>
                {!valid && this.state.dirty && (
                    <Alert bsStyle='danger'>
                        The items below are required...
                    </Alert>
                )}

                <div style={{ marginBottom: 10 }}>
                    <h3 style={{ marginBottom: 0 }}>{this.state.id > 0 ? 'Edit Notification' : 'Add Notification'}</h3>
                    {this.props.clone.isClone && (
                        <div style={{ fontSize: 12 }}>(Cloned from {this.props.clone.description})</div>
                    )}
                </div>

                <FormGroup validationState={this.state.dirty && !this.state.validation.center ? 'error' : null}>
                    <ControlLabel>Center</ControlLabel>
                    <FormControl
                        componentClass='select'
                        value={this.state.center}
                        onChange={this.handleCenterChange}
                    >
                        <option value={0}>- SELECT -</option>
                        {this.props.centerList.filter(x => x.isMemberCenter).map((center, index) =>
                            <option key={`center-${index}`} value={center.id}>{center.alias}</option>
                        )}
                    </FormControl>
                </FormGroup>


                <FormGroup validationState={this.state.dirty && !this.state.validation.center ? 'error' : null}>
                    <ControlLabel>Notification Type</ControlLabel>
                    <FormControl
                        componentClass='select'
                        value={this.state.notificationTypeId}
                        onChange={this.handleNotificationChange}
                    >
                        <option value={0}>- SELECT -</option>
                        {this.props.notificationList.map((notetype, index) =>
                            <option key={`note-${index}`} value={notetype.id}>{notetype.name}</option>
                        )}
                    </FormControl>
                </FormGroup>

                <FormGroup validationState={this.state.dirty && !this.state.validation.description ? 'error' : null}>
                    <ControlLabel>Description</ControlLabel>
                    <FormControl
                        componentClass='textarea'
                        value={this.state.description}
                        onChange={this.handleDescriptionChange}
                    />
                </FormGroup>

                <FormGroup validationState={this.state.dirty && !this.state.validation.content ? 'error' : null}>
                    <ControlLabel>Content</ControlLabel>
                    <EditorWrapper error={contentValid}>
                        <JoditEditor
                            value={this.state.content}
                            config={config}
                            onChange={this.handleContentChange}
                            onBlur={() => null}
                        />
                    </EditorWrapper>
                </FormGroup>

                <FormGroup>
                    <Checkbox checked={this.state.isEnabled} onClick={() => this.setState({ isEnabled: !this.state.isEnabled })}>Enabled</Checkbox>
                </FormGroup>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Button bsStyle='primary' bsSize='large' style={{ marginRight: 5 }} onClick={this.handleSave}>Save</Button>
                    <Button bsStyle='link' onClick={this.handleCancel}>Cancel</Button>
                </div>
            </div>
        );
    }
}

export default AddEdit;